import { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Instagram, Youtube, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SITE } from "../data/site";
import { TikTokIcon } from "./icons/TikTokIcon";
import { trackEvent, attachCampaign } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = ["Adult Classes", "USDD Kids", "Qualifications", "BA Pathway", "Events / Battles", "Other"];

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in your name, email, phone and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, attachCampaign(form));
      trackEvent("contact_submit", { interest: form.interest || "general" });
      toast.success("Message sent. We'll be in touch soon.");
      setForm({ name: "", email: "", phone: "", interest: "", message: "" });
      navigate("/thank-you?reason=enquiry");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-brand-purple/25 blur-[150px]" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-brand-yellow" />
            <span className="text-xs font-bold uppercase tracking-[0.3em] text-brand-yellow">12 · Get in touch</span>
          </div>
          <h2 className="font-display font-black uppercase text-4xl sm:text-5xl lg:text-6xl text-white tracking-tighter leading-[0.95]">
            Let's <span className="italic text-brand-yellow text-glow-yellow">move</span>.
          </h2>
          <p className="mt-6 text-zinc-400 max-w-md text-base leading-relaxed">
            Whether you're joining a class, enquiring about courses or
            booking a workshop — drop us a line and our team will reply within 24 hours.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`mailto:${SITE.contact.email}`} data-testid="contact-email-link" className="group flex items-start gap-4 text-zinc-200 hover:text-brand-yellow transition-colors">
              <Mail className="text-brand-yellow mt-1 flex-shrink-0" size={20} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Email</div>
                <div className="font-bold text-base">{SITE.contact.email}</div>
              </div>
            </a>
            <a href={`tel:${SITE.contact.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="group flex items-start gap-4 text-zinc-200 hover:text-brand-yellow transition-colors">
              <Phone className="text-brand-yellow mt-1 flex-shrink-0" size={20} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Phone</div>
                <div className="font-bold text-base">{SITE.contact.phone}</div>
              </div>
            </a>
            <div className="flex items-start gap-4 text-zinc-200">
              <MapPin className="text-brand-yellow mt-1 flex-shrink-0" size={20} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Where we teach</div>
                <div className="font-bold text-base">London & Kent</div>
                <div className="text-xs text-zinc-400 mt-2 leading-relaxed">
                  Classes & programmes delivered across multiple studios — Medway, Chatham, Elephant & Castle and surrounding areas.
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 pt-8 border-t border-white/10">
            <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-4">Follow the movement</div>
            <div className="flex flex-wrap gap-3">
              <a href={SITE.socials.instagram_main.url} target="_blank" rel="noreferrer" data-testid="social-instagram-main" className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 hover:border-brand-yellow hover:bg-brand-yellow hover:text-black text-zinc-200 text-sm font-bold transition-all">
                <Instagram size={16} />
                {SITE.socials.instagram_main.handle}
              </a>
              <a href={SITE.socials.instagram_kids.url} target="_blank" rel="noreferrer" data-testid="social-instagram-kids" className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 hover:border-brand-yellow hover:bg-brand-yellow hover:text-black text-zinc-200 text-sm font-bold transition-all">
                <Instagram size={16} />
                {SITE.socials.instagram_kids.handle}
              </a>
              <a href={SITE.socials.instagram_media.url} target="_blank" rel="noreferrer" data-testid="social-instagram-media" className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 hover:border-brand-yellow hover:bg-brand-yellow hover:text-black text-zinc-200 text-sm font-bold transition-all">
                <Instagram size={16} />
                {SITE.socials.instagram_media.handle}
              </a>
              <a href={SITE.socials.youtube.url} target="_blank" rel="noreferrer" data-testid="social-youtube" className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 hover:border-brand-yellow hover:bg-brand-yellow hover:text-black text-zinc-200 text-sm font-bold transition-all">
                <Youtube size={16} />
                {SITE.socials.youtube.handle}
              </a>
              <a href={SITE.socials.tiktok_networks.url} target="_blank" rel="noreferrer" data-testid="social-tiktok-networks" className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 hover:border-brand-yellow hover:bg-brand-yellow hover:text-black text-zinc-200 text-sm font-bold transition-all">
                <TikTokIcon size={14} />
                {SITE.socials.tiktok_networks.handle}
              </a>
              <a href={SITE.socials.tiktok_kids.url} target="_blank" rel="noreferrer" data-testid="social-tiktok-kids" className="group flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/15 hover:border-brand-yellow hover:bg-brand-yellow hover:text-black text-zinc-200 text-sm font-bold transition-all">
                <TikTokIcon size={14} />
                {SITE.socials.tiktok_kids.handle}
              </a>
            </div>
          </div>
        </div>

        <motion.form
          onSubmit={onSubmit}
          data-testid="contact-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-7 p-8 sm:p-10 rounded-3xl bg-zinc-950/70 border border-white/10 backdrop-blur-xl"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field label="Full name *" >
              <input
                data-testid="contact-name-input"
                type="text"
                value={form.name}
                onChange={update("name")}
                className="input"
                placeholder="Your name"
              />
            </Field>
            <Field label="Email *">
              <input
                data-testid="contact-email-input"
                type="email"
                value={form.email}
                onChange={update("email")}
                className="input"
                placeholder="you@email.com"
              />
            </Field>
            <Field label="Phone / WhatsApp *">
              <input
                data-testid="contact-phone-input"
                type="tel"
                inputMode="tel"
                value={form.phone}
                onChange={update("phone")}
                className="input"
                placeholder="+44 7xxx xxxxxx (international welcome)"
              />
            </Field>
            <Field label="I'm interested in">
              <select
                data-testid="contact-interest-select"
                value={form.interest}
                onChange={update("interest")}
                className="input appearance-none cursor-pointer"
              >
                <option value="">Select an option</option>
                {INTERESTS.map((i) => (
                  <option key={i} value={i}>{i}</option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Message *" className="mt-5">
            <textarea
              data-testid="contact-message-input"
              value={form.message}
              onChange={update("message")}
              rows={5}
              className="input resize-none"
              placeholder="Tell us what you're looking for..."
            />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-btn"
            className="mt-8 group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-brand-yellow text-black font-black uppercase text-sm tracking-wider hover:bg-brand-yellowHover hover:scale-105 transition-all glow-yellow disabled:opacity-60 disabled:hover:scale-100"
          >
            {submitting ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />}
            {submitting ? "Sending..." : "Send message"}
          </button>

          <style>{`
            .input {
              width: 100%;
              background: rgba(255,255,255,0.03);
              border: 1px solid rgba(255,255,255,0.1);
              border-radius: 12px;
              padding: 14px 16px;
              color: white;
              font-size: 14px;
              font-family: 'Manrope', sans-serif;
              transition: all 0.2s;
              outline: none;
            }
            .input::placeholder { color: rgba(255,255,255,0.35); }
            .input:focus {
              border-color: #E5FF00;
              box-shadow: 0 0 0 3px rgba(229,255,0,0.15);
              background: rgba(255,255,255,0.05);
            }
            select.input option { background: #0a0a0a; color: white; }
          `}</style>
        </motion.form>
      </div>
    </section>
  );
};

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">{label}</span>
    {children}
  </label>
);
